export { SETTABLE_TICKET_STATUSES, TICKET_CHANNELS, TICKET_PRIORITIES, TICKET_STATUSES, escalationTargets, isEscalation } from './chunk-G3V7WW22.js';
import { createContextApi, invalidatesOne, CACHE_POLICY, providesOne, providesList, createCursorPagedEndpoint, registerCrossContextInvalidation } from '@quickbee/store';

var HELPDESK_TAGS = [
  "Ticket",
  "Agent",
  "AgentWorkload",
  "CalendarEvent",
  "SlaDashboard",
  // Help content. `Article` and `ArticleCollection` are separate tags because the
  // grouped Knowledge Base read depends on both: renaming a collection must
  // refetch it without invalidating every flat FAQ list in every other app.
  "Article",
  "ArticleCollection"
];
var WORKLOAD_CURRENT = { type: "AgentWorkload", id: "CURRENT" };
var CALENDAR_LIST = { type: "CalendarEvent", id: "LIST" };
var SLA_CURRENT = { type: "SlaDashboard", id: "CURRENT" };
var TICKET_LIST = { type: "Ticket", id: "LIST" };
var ARTICLE_LIST = { type: "Article", id: "LIST" };
var COLLECTION_LIST = { type: "ArticleCollection", id: "LIST" };
var helpdeskEndpoints = (build) => ({
  /**
   * Returns the *summary* projection, not {@link TicketResponse} — the list has
   * no SLA timer and no assignment history, so a row cannot stand in for a
   * detail fetch.
   */
  listTickets: build.query({
    query: (params) => ({ url: "/helpdesk/tickets", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Ticket", result),
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  getTicket: build.query({
    query: (id) => ({ url: `/helpdesk/tickets/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Ticket", id)
  }),
  /**
   * A self-service caller must send a non-blank `description` (the server
   * enforces it for own-scoped callers only) and its `customerName`/`assigneeId`
   * are ignored — requester identity comes from the `customer_id` claim (ADR-001).
   */
  createTicket: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/helpdesk/tickets",
      method: "POST",
      body,
      idempotencyKey: `ticket-create:${draftId}`
    }),
    // A new ticket moves the open count, can arrive pre-assigned, and puts a new
    // due-date on the calendar feed.
    invalidatesTags: [TICKET_LIST, WORKLOAD_CURRENT, CALENDAR_LIST, SLA_CURRENT]
  }),
  /**
   * Full replacement of the editable fields — `subject`, `channel` and
   * `priority` are all required server-side, so send the whole edit form rather
   * than the changed fields.
   */
  updateTicket: build.mutation({
    query: ({ id, requestId, ...body }) => ({
      url: `/helpdesk/tickets/${id}`,
      method: "PATCH",
      body,
      idempotencyKey: `ticket-update:${id}:${requestId}`
    }),
    // Priority is editable here, and the SLA widget counts breaches by priority.
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Ticket", id),
      SLA_CURRENT
    ]
  }),
  /** Assign or reassign. There is no unassign — the endpoint requires an `agentId`. */
  updateAssignee: build.mutation({
    query: ({ id, agentId, requestId }) => ({
      url: `/helpdesk/tickets/${id}/assignee`,
      method: "PATCH",
      body: { agentId },
      idempotencyKey: `ticket-assign:${id}:${requestId}`
    }),
    // The optimistic patch lives in index.ts (`enhanceEndpoints`), because it
    // needs a reference to the slice these endpoints are used to build.
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Ticket", id),
      WORKLOAD_CURRENT
    ]
  }),
  /**
   * Only `PENDING` and `WAITING_ON_CUSTOMER` are reachable here — resolution goes
   * through `closeTicket`, `CLOSED` is sweep-only (ADR-003), and `OPEN` is not
   * directly settable. `SettableTicketStatus` makes the other three untypeable.
   */
  updateStatus: build.mutation({
    query: ({ id, targetStatus, requestId }) => ({
      url: `/helpdesk/tickets/${id}/status`,
      method: "PATCH",
      body: { targetStatus },
      idempotencyKey: `ticket-status:${id}:${requestId}`
    }),
    // WAITING_ON_CUSTOMER pauses the SLA timer and moves the headline counts.
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Ticket", id),
      SLA_CURRENT
    ]
  }),
  /**
   * The console's "Close Ticket" action, which *resolves* the ticket (ADR-003) —
   * permanent close is the sweep's job. Takes no body: there is no resolution
   * note on this endpoint.
   */
  closeTicket: build.mutation({
    query: ({ id, requestId }) => ({
      url: `/helpdesk/tickets/${id}/close`,
      method: "POST",
      idempotencyKey: `ticket-close:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Ticket", id),
      WORKLOAD_CURRENT,
      CALENDAR_LIST,
      SLA_CURRENT
    ]
  }),
  /** `newPriority` must be strictly more urgent than the current one (INV-15). */
  escalateTicket: build.mutation({
    query: ({ id, requestId, ...body }) => ({
      url: `/helpdesk/tickets/${id}/escalate`,
      method: "POST",
      body,
      idempotencyKey: `ticket-escalate:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Ticket", id),
      SLA_CURRENT
    ]
  }),
  /** Reopens a `RESOLVED` ticket (INV-12). Takes no body. */
  reopenTicket: build.mutation({
    query: ({ id, requestId }) => ({
      url: `/helpdesk/tickets/${id}/reopen`,
      method: "POST",
      idempotencyKey: `ticket-reopen:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Ticket", id),
      WORKLOAD_CURRENT,
      CALENDAR_LIST,
      SLA_CURRENT
    ]
  }),
  /**
   * The requester's one-time CSAT score (INV-16). Keyed on the ticket id alone —
   * see the file header for why this one is deliberately not `requestId`-keyed.
   */
  rateSatisfaction: build.mutation({
    query: ({ id, ...body }) => ({
      url: `/helpdesk/tickets/${id}/satisfaction`,
      method: "POST",
      body,
      idempotencyKey: `ticket-csat:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Ticket", id)
  }),
  getAgentWorkload: build.query({
    query: () => ({ url: "/helpdesk/tickets/agent-workload" }),
    providesTags: [WORKLOAD_CURRENT],
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  getAssignableAgents: build.query({
    query: () => ({ url: "/helpdesk/assignable-agents" }),
    // Keyed on `agentId`: these rows carry no `id`, and the default key would
    // silently degrade the tag set to a bare LIST entry.
    providesTags: (result) => providesList("Agent", result, "agentId"),
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /** `from`/`to` are both required by the controller — an omitted bound is a 400. */
  getCalendarEvents: build.query({
    query: ({ from, to }) => ({ url: "/helpdesk/calendar-events", params: { from, to } }),
    // Rows have no identifier of their own, so the feed invalidates as a whole.
    providesTags: [CALENDAR_LIST],
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  getSlaStatus: build.query({
    query: () => ({ url: "/helpdesk/dashboard/sla-status" }),
    providesTags: [SLA_CURRENT],
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  // ── Help content ───────────────────────────────────────────────────────────
  //
  // Cached as `reference`, not `transactionalList`: help content changes when an
  // author edits it, not when a customer transacts, so the aggressive refetch
  // cadence the ticket lists need would be pure waste on a Help page that is
  // opened and closed.
  /**
   * The flat FAQ list — what every app's Help page renders.
   *
   * Always pass `surface`. Omitting it returns every surface's FAQ, so a
   * portfolio Help page would silently show POS and admin answers too.
   */
  listArticles: build.query({
    query: (params) => ({ url: "/helpdesk/articles", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Article", result),
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  getArticle: build.query({
    query: (id) => ({ url: `/helpdesk/articles/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Article", id),
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /**
   * Collections with their articles nested — the Knowledge Base page, and any
   * Help page that wants its FAQ grouped rather than flat.
   *
   * Unpaged by design: a surface's help corpus is reference data in the tens.
   * `type` defaults to `FAQ` server-side, so the KB page must send
   * `KNOWLEDGE_BASE` explicitly or it will render the FAQ corpus instead.
   */
  getKnowledgeBase: build.query({
    query: ({ surface, type }) => ({
      url: "/helpdesk/knowledge-base",
      params: type ? { surface, type } : { surface }
    }),
    // Provides both tags: the response is collections *and* articles, so either
    // kind of edit has to be able to invalidate it.
    providesTags: [ARTICLE_LIST, COLLECTION_LIST],
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /** `draftId` scopes the idempotency key to one authoring session, per §I-idempotency. */
  createArticle: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/helpdesk/articles",
      method: "POST",
      body,
      idempotencyKey: `article-create:${draftId}`
    }),
    invalidatesTags: [ARTICLE_LIST, COLLECTION_LIST]
  }),
  /**
   * Full replacement — send the whole edit form, not the changed fields.
   *
   * The key carries `requestId`, not just `id`, because editing is repeatable:
   * the filter keys on `(tenantId, key)` alone with a 24-hour TTL, so an
   * id-only key would replay the *first* edit's response for every later edit
   * that day and the second correction to an article would silently no-op.
   */
  updateArticle: build.mutation({
    query: ({ id, requestId, ...body }) => ({
      url: `/helpdesk/articles/${id}`,
      method: "PUT",
      body,
      idempotencyKey: `article-update:${id}:${requestId}`
    }),
    // COLLECTION_LIST too: an update can move the article between collections,
    // which changes the grouped read on both sides of the move.
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Article", id),
      ARTICLE_LIST,
      COLLECTION_LIST
    ]
  }),
  /**
   * Publish (`PUBLISHED`), unpublish (`DRAFT`) or archive (`ARCHIVED`).
   *
   * `requestId` is mandatory here for the same reason as the edit, and more
   * acutely: publish → unpublish → publish is an ordinary authoring sequence,
   * and an id-only key would replay the first publish and leave the article
   * live while the UI reported it as taken down.
   */
  changeArticleStatus: build.mutation({
    query: ({ id, requestId, targetStatus }) => ({
      url: `/helpdesk/articles/${id}/status`,
      method: "PATCH",
      body: { targetStatus },
      idempotencyKey: `article-status:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Article", id),
      ARTICLE_LIST,
      COLLECTION_LIST
    ]
  }),
  /** Returns the new collection's id, not the collection — the server issues a bare `201`. */
  createCollection: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/helpdesk/knowledge-base/collections",
      method: "POST",
      body,
      idempotencyKey: `article-collection-create:${draftId}`
    }),
    invalidatesTags: [COLLECTION_LIST]
  }),
  /** Returns `204 No Content` — the refreshed row arrives through tag invalidation. */
  updateCollection: build.mutation({
    query: ({ id, requestId, ...body }) => ({
      url: `/helpdesk/knowledge-base/collections/${id}`,
      method: "PUT",
      body,
      idempotencyKey: `article-collection-update:${id}:${requestId}`
    }),
    invalidatesTags: [COLLECTION_LIST, ARTICLE_LIST]
  }),
  /**
   * Archive or restore. `204 No Content`.
   *
   * Invalidates `ARTICLE_LIST` as well: archiving a collection hides its
   * articles from every reader query without touching their own statuses, so a
   * flat FAQ list that only watched `ArticleCollection` would keep serving them.
   */
  changeCollectionStatus: build.mutation({
    query: ({ id, requestId, targetStatus }) => ({
      url: `/helpdesk/knowledge-base/collections/${id}/status`,
      method: "PATCH",
      body: { targetStatus },
      idempotencyKey: `article-collection-status:${id}:${requestId}`
    }),
    invalidatesTags: [COLLECTION_LIST, ARTICLE_LIST]
  })
});

// src/index.ts
var baseHelpdeskApi = createContextApi({
  context: "helpdesk",
  reducerPath: "helpdeskApi",
  tagTypes: HELPDESK_TAGS,
  mocks: () => import('./mocks-4EXKEMPK.js').then((module) => module.helpdeskMocks),
  endpoints: helpdeskEndpoints
});
var helpdeskApi = baseHelpdeskApi.enhanceEndpoints({
  endpoints: {
    /**
     * `currentAssignee` is an object, not an id, so an honest optimistic patch
     * needs the agent's display name too. It is read from the already-cached
     * assignable-agents list; when that cache is cold (or the agent is not in
     * it), the patch is skipped entirely rather than rendering a placeholder
     * name that would flicker to the real one a moment later. The `invalidatesTags`
     * refetch still corrects the row either way.
     */
    updateAssignee: {
      async onQueryStarted({ id, agentId }, { dispatch, getState, queryFulfilled }) {
        const agents = baseHelpdeskApi.endpoints.getAssignableAgents.select()(
          getState()
        ).data;
        const agent = agents?.find((candidate) => candidate.agentId === agentId);
        if (!agent) {
          queryFulfilled.catch(() => void 0);
          return;
        }
        const assignment = {
          agentId,
          agentName: agent.displayName,
          assignedAt: (/* @__PURE__ */ new Date()).toISOString()
        };
        const patch = dispatch(
          baseHelpdeskApi.util.updateQueryData("getTicket", id, (draft) => {
            draft.currentAssignee = assignment;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      }
    },
    updateStatus: {
      async onQueryStarted({ id, targetStatus }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          baseHelpdeskApi.util.updateQueryData("getTicket", id, (draft) => {
            draft.status = targetStatus;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      }
    }
  }
});
var HELPDESK_INVALIDATION_RULES = [
  {
    event: "ticket.changed",
    // The list and the SLA widget are invalidated alongside the ticket itself:
    // the announced change may be a ticket this client has never fetched (the
    // `DisputeRaised` auto-creation path), which a detail-only tag would miss.
    invalidate: ({ ticketId }) => ticketId ? [
      { type: "Ticket", id: ticketId },
      { type: "Ticket", id: "LIST" },
      { type: "SlaDashboard", id: "CURRENT" }
    ] : [],
    // Already a helpdesk-context fact when emitted — no outbox hop to wait for.
    settleDelayMs: 0
  }
];
function registerHelpdeskInvalidation(store) {
  return registerCrossContextInvalidation({
    store,
    api: helpdeskApi,
    rules: HELPDESK_INVALIDATION_RULES
  });
}
var {
  useListTicketsQuery,
  useGetTicketQuery,
  useCreateTicketMutation,
  useUpdateTicketMutation,
  useUpdateAssigneeMutation,
  useUpdateStatusMutation,
  useCloseTicketMutation,
  useEscalateTicketMutation,
  useReopenTicketMutation,
  useRateSatisfactionMutation,
  useGetAgentWorkloadQuery,
  useGetAssignableAgentsQuery,
  useGetCalendarEventsQuery,
  useGetSlaStatusQuery,
  // Help content
  useListArticlesQuery,
  useGetArticleQuery,
  useGetKnowledgeBaseQuery,
  useCreateArticleMutation,
  useUpdateArticleMutation,
  useChangeArticleStatusMutation,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useChangeCollectionStatusMutation
} = helpdeskApi;

export { HELPDESK_INVALIDATION_RULES, HELPDESK_TAGS, helpdeskApi, registerHelpdeskInvalidation, useChangeArticleStatusMutation, useChangeCollectionStatusMutation, useCloseTicketMutation, useCreateArticleMutation, useCreateCollectionMutation, useCreateTicketMutation, useEscalateTicketMutation, useGetAgentWorkloadQuery, useGetArticleQuery, useGetAssignableAgentsQuery, useGetCalendarEventsQuery, useGetKnowledgeBaseQuery, useGetSlaStatusQuery, useGetTicketQuery, useListArticlesQuery, useListTicketsQuery, useRateSatisfactionMutation, useReopenTicketMutation, useUpdateArticleMutation, useUpdateAssigneeMutation, useUpdateCollectionMutation, useUpdateStatusMutation, useUpdateTicketMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map